import { ReactElement, useContext, useEffect, useState } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import {
  IAddition,
  IDelayBase, IDictionary, IEAFProduction, IProduction,
  IProductionExtended, ISelectedNode,
  IShiftHistory, IState, IStrandProduction,
  IVariableGroup
} from "../../../types";
import { Paper, useTheme } from "@mui/material";
import KeyValueItem from "../../../components/charts/keyValueItem";
import Detail from "../../../components/dialogs/detail";
import HeatInformation from "./heatInformation";
import {
  formatDate,
  formatDateTimeWithSeconds,
  formatTimeWithSeconds,
  getCurrentDatetimeInSeconds
} from "../../../system/utils";
import { Alert } from "@mui/material";
import GanttSlider from "./ganttSlider";
import {
  xAdditionAccessorScaled,
  xProductionBeginAccessorScaled,
  xProductionEndAccessorScaled,
  xSampleAccessorScaled
} from "../utils";
import { SystemContext } from "../../../context/systemContext";
import { getLanguage } from "../configuration/language";
import { getPalette } from "../../../configuration/configuration";
import SkeletonGantt from "./skeletonGantt";
import { DateTime } from "luxon";
import ProductionInformation from "../../mill/components/productionInformation";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import HtmlTooltip from "../../../components/charts/tooltip";
import AdditionsLayerGantt from "./additionsLayerGantt";
import VariablesLayerGantt from "../variablesLayerGantt";
import SamplesLayerGantt from "./samplesLayerGantt";
import HeatDetailInfo from "../../meltShop/components/heatDetailInfo";
import StrandDetailInfo from "../../mill/components/strandDetailInfo";
import LRFHeatInformation from "../../meltShop/lrf/lrfHeatInformation";
import CasterHeatInformation from "../../meltShop/caster/casterHeatInformation";
import { useSelector } from "react-redux";

interface IHeatChartProps {
  productionData: IProduction[];
  parentSize: {
    width: number;
    height: number;
  } | null;
}

const productTooltip = (productionItem: IProductionExtended, detail: ReactElement | null, children: ReactElement): ReactElement => {
  return(
    <HtmlTooltip title={
      <div>
        <div className="display-flex-column align-items-center" >
          <strong>{productionItem.machine.name}</strong>
          <strong>{productionItem.title}</strong>
        </div>
        <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionItem.beginDatetime as number)} />
        <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionItem.endDatetime as number)} />
        {detail}
      </div>
    }>
      {children}
    </HtmlTooltip>
  );
};

const heatHeight = 45;
// const sliderHeight =  30;
const marginTop = 40;
const marginBottomWithSlider = 95;
const shiftHeader = 30;

const GanttChart = ({ productionData, parentSize }: IHeatChartProps): ReactElement => {
  const chartID =useUniqueId("gantt-chart");

  let rowsPerPage = productionData.length;
  let height = 30 + productionData.length * heatHeight;
  if (parentSize) {
    rowsPerPage =  Math.trunc((parentSize.height - marginTop -  marginBottomWithSlider - shiftHeader) / heatHeight);
    height = rowsPerPage * heatHeight + marginBottomWithSlider + marginTop + shiftHeader;
  }

  const useScroll = rowsPerPage < productionData.length;

  const theme = useTheme();
  const systemContext = useContext(SystemContext);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [openDetail, setOpenDetail] = useState(false);
  const [title, setTitle] = useState("");
  const [shifts, setShifts] = useState<IShiftHistory[]>([]);
  const [window, setWindow] = useState({
    beginDatetime: 0,
    maxEndDatetime: 0,
    endDatetime: 0,
    dateFactor: 0,
    window: 0,
    dateOffset: 0,
    previous: 0,
    forward: true
  });

  const [selectedProductionItem, setSelectedProductionItem] = useState<IProductionExtended | null>(null);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height,
    marginBottom: !useScroll ? 65 : 95,
    marginLeft: 40,
    marginRight: 40,
    marginTop
  });


  const showMachineName = selectedNode.children && selectedNode.children.length > 1;

  useEffect(() => {
    if (productionData && productionData.length > 0 && parentSize) {

      const shiftList: IDictionary<IShiftHistory> = {};

      let min = DateTime.now().toSeconds();
      let max = 0;
      let maxEndTime = 0;

      productionData.forEach(x => {
        x.productionMachines.forEach(s => {
          const key = `${s.shiftHistory.factoryDay}-${s.shiftHistory.name}`;
          if (!shiftList[key]) {
            shiftList[key] = s.shiftHistory;
          }

          min = Math.min(min, s.shiftHistory.factoryDay);
          max = Math.max(max, s.shiftHistory.factoryDay);
          maxEndTime = Math.max(maxEndTime, s.shiftHistory.endDatetime);
        });
      });


      const beginDatetime = productionData[0].beginDatetime;
      const maxEndDatetime = Math.max(maxEndTime, productionData[productionData.length - 1].endDatetime ?? getCurrentDatetimeInSeconds());
      const endDatetime = productionData.length <= rowsPerPage
        ? Math.max(maxEndDatetime, productionData[Math.min(rowsPerPage - 1, productionData.length - 1)].endDatetime ?? getCurrentDatetimeInSeconds())
        : productionData[Math.min(rowsPerPage - 1, productionData.length - 1)].endDatetime ?? getCurrentDatetimeInSeconds();

      const dateFactor = maxEndDatetime - beginDatetime;

      const newWindow = { beginDatetime, endDatetime, maxEndDatetime, dateFactor, window: (endDatetime - beginDatetime) / (maxEndDatetime - beginDatetime), dateOffset: 0, previous: 0, forward: true };

      setWindow(newWindow);
      setTitle(min !== max ? `${formatDate(min)} - ${formatDate(max)}` : `${formatDate(min)}`);
      setShifts(Object.values(shiftList).sort((a, b) => a.beginDatetime - b.beginDatetime));
    }
  }, [productionData, parentSize, rowsPerPage]);

  if (productionData.length === 0) {
    return (
      <Paper>
        <Alert severity="warning">No Data to Display.</Alert>
      </Paper>
    );
  }

  if (window.beginDatetime === 0) {
    return <SkeletonGantt/>;
  }

  const xScale = d3.scaleLinear()
    .domain([window.beginDatetime + window.dateOffset, window.endDatetime + window.dateOffset])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDelayBase) => xScale(d.endDatetime ?? getCurrentDatetimeInSeconds()) - xScale(d.beginDatetime);

  const onSelected = (productionItem: IProductionExtended) => {
    setSelectedProductionItem(productionItem);
    setOpenDetail(true);
  };

  const onOffsetChange= (newOffset: number) => {
    const newValue = window.dateFactor * newOffset;
    const forward = window.previous < newOffset;

    setWindow({ ...window, dateOffset: newValue,  previous: newOffset, forward });
  };

  const productionFiltered = productionData.filter(x => (x.endDatetime ?? getCurrentDatetimeInSeconds()) >= window.beginDatetime  + window.dateOffset && x.beginDatetime < window.endDatetime  + window.dateOffset);

  const sliceRange = window.forward
    ? [Math.max(productionFiltered.length - rowsPerPage - 1, 0), productionFiltered.length]
    : [0, Math.min( rowsPerPage, productionFiltered.length)];

  const isHeat = productionData[0].productionMachines[0].__typename === "EAFProduction";


  return (
    <Paper ref={ref} style={{ height: dimensions.height }} elevation={0}>
      <Chart dimensions={dimensions}>
        <defs>
          <clipPath id="cut-off">
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={title}
          ticks={getTicks(xScale.domain(), parentSize && parentSize.width > 600 ? 5 : 3)}
          formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>

        <g>
          {useScroll &&
            <GanttSlider width={dimensions.boundedWidth}
              top={dimensions.boundedHeight + 50}
              percent={window.window}
              onOffsetChange={onOffsetChange}/>
          }
        </g>

        <g transform={`translate(${0}, ${-20})`}>
          <g transform={`translate(${0}, ${-10})`}>
            <polygon fill={"green"} points="5 0 0 5 5 10 10 5 5 0"/>
          </g>
          <text y={0}
            x={12}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).samples}
          </text>

          <g transform={`translate(${78}, ${-10})`}>
            <polygon fill={"#d84315"} points="0 0 5 10 10 0 0 0"/>
          </g>
          <text y={0}
            x={90}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).variables}
          </text>

          {isHeat &&
            <>
              <circle fill={"purple"} cx={163} cy={-5} r={5}/>
              <text y={0}
                x={170}
                fill={theme.palette.text.primary}
                fontSize="1em"
                fillOpacity="0.9"
                textAnchor="start">
                {getLanguage(systemContext.languageCode).buckets}
              </text>

              <rect fill={"blue"} x={238} y={-9} width={8} height={8}/>
              <text y={0}
                x={250}
                fill={theme.palette.text.primary}
                fontSize="1em"
                fillOpacity="0.9"
                textAnchor="start">
                {getLanguage(systemContext.languageCode).alloys}
              </text>
            </>
          }

        </g>

        <g>
          {shifts.map((shift: IShiftHistory, shiftIndex: number): ReactElement | null => {

            const shiftStart = xScale(shift.beginDatetime);
            const shiftEnd = xScale(shift.endDatetime);

            if (shiftStart > dimensions.boundedWidth || shiftEnd < 0) {
              return null;
            }

            const shiftWidth = shiftEnd - shiftStart;

            return(
              <g key={`shift-area-${shiftIndex}`} clipPath="url(#cut-off)">
                <rect
                  key={`shift-${shiftIndex}`}
                  style={{ fill: shiftIndex % 2 === 0 ? "#ECEFF1" : "#CFD8DC" }}
                  fillOpacity={0.5}
                  x={shiftStart}
                  y={0}
                  width={Math.min(shiftWidth, dimensions.boundedWidth - shiftStart)}
                  height={dimensions.boundedHeight} />

                <text y={17}
                  x={xScale(shift.beginDatetime) + 5}
                  fill={theme.palette.text.primary}
                  fontWeight="bold"
                  fontSize="1em"
                  fillOpacity="0.9"
                  textAnchor="start">
                  {`${shift.name} - ${formatDate(shift.factoryDay)}`}
                </text>

                <line className="Axis__line" strokeWidth="1" x1={shiftStart} x2={shiftStart} y1={0} y2={dimensions.boundedHeight + 20} />

                <text y={dimensions.boundedHeight + 15}
                  x={shiftStart + 5}
                  fill={theme.palette.text.primary}
                  fontSize="0.8em"
                  textAnchor="start">
                  {formatTimeWithSeconds(shift.beginDatetime as number)}
                </text>
              </g>
            );
          })}
        </g>

        <g clipPath="url(#cut-off)">
          {productionFiltered.slice(sliceRange[0], sliceRange[1]).map((production: IProduction, productionIndex: number) => {
            const heatGroup = (productionIndex + 1) * heatHeight;
            const productionKey = `${chartID}-production-${production.productionID}`;
            const lastProductionItem = production.productionMachines[production.productionMachines.length - 1];
            const referencePos: { x: number; align: "start" | "end" } = {
              x: xProductionEndAccessorScaled(lastProductionItem, xScale) + 5,
              align: "start"
            };

            if (referencePos.x > dimensions.boundedWidth) {
              referencePos.x = xProductionBeginAccessorScaled(production.productionMachines[0], xScale) - 5;
              referencePos.align = "end";
            }

            return (
              <g key={productionKey}>
                <rect
                  fill={productionIndex % 2 === 0 ? "#fff" : "#ddd"}
                  fillOpacity={0.4}
                  x={0}
                  y={heatGroup - 20}
                  width={dimensions.boundedWidth}
                  height={heatHeight} />
                <line className="Axis__line" strokeWidth="0.5" x1={0} x2={dimensions.boundedWidth} y1={heatGroup-20} y2={heatGroup - 20} />

                {production.productionMachines.map((productionItem: IProductionExtended, heatIndex: number) => {
                  const heatKey = `${productionKey}-${productionItem.title}-${heatIndex}`;
                  const heatY = 0;

                  const variables: IVariableGroup[] = [];
                  productionItem.variables.forEach(variable => {
                    const item = variables.find(x => x.datetime === variable.datetime);
                    if (!item) {
                      variables.push({
                        datetime: variable.datetime,
                        variables: [ variable ]
                      });
                    } else {
                      item.variables.push(variable);
                    }
                  });

                  const heatData = productionItem.__typename === "EAFProduction" ? productionItem as IEAFProduction : null;
                  const rmData = productionItem.__typename === "StrandProduction" ? productionItem as IStrandProduction : null;
                  const productWidth = Math.max(xProductionEndAccessorScaled(productionItem, xScale) - xProductionBeginAccessorScaled(productionItem, xScale), 0);
                  const flag = productWidth / productionItem.title.length > 8.3;

                  return(
                    <g key={heatKey} transform={`translate(0, ${heatGroup})`}  height={heatHeight} onClick={() => onSelected(productionItem)}>
                      {productTooltip(
                        productionItem,
                        heatData && HeatDetailInfo(heatData) || rmData && StrandDetailInfo(rmData),
                        <rect
                          cursor="pointer"
                          fill={getPalette().statusColor.production.background}
                          x={xProductionBeginAccessorScaled(productionItem, xScale)}
                          y={heatY}
                          width={productWidth}
                          height={20}
                        />)}

                      <DelayLayerGantt delays={productionItem.delays} height={20} y={heatY} xAccessorScaled={xAccessorScaled}
                        widthAccessorScaled={widthAccessorScaled} selected={null} chartKey={chartID} />

                      <SamplesLayerGantt chartID={heatKey} title={productionItem.title}
                        samples={productionItem.samples}
                        y={heatY - 13} xAccessorScaled={sample => xSampleAccessorScaled(sample, xScale) - 4} />

                      <VariablesLayerGantt chartID={heatKey} title={productionItem.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

                      {heatData &&
                        <AdditionsLayerGantt chartID={heatKey} title={productionItem.title}
                          additions={heatData.additions}
                          xAccessorScaled={(addition: IAddition) => xAdditionAccessorScaled(addition, xScale)}
                          y={heatY - 12}/>}

                      {productTooltip(
                        productionItem,
                        heatData && HeatDetailInfo(heatData) || rmData && StrandDetailInfo(rmData),
                        <text
                          y={heatY + 15}
                          x={xProductionBeginAccessorScaled(productionItem, xScale) + (xProductionEndAccessorScaled(productionItem, xScale) - xProductionBeginAccessorScaled(productionItem, xScale)) / 2 }
                          fontSize="1em"
                          textAnchor="middle"
                          fill={flag ? "white" : "#444"}
                          fontWeight={"bold"}
                          onClick={() => onSelected(productionItem)}>
                          {showMachineName ? productionItem.machine.name : productionItem.reference ?? productionItem.title}
                        </text>)}
                    </g>
                  );
                })}

                {showMachineName && <text
                  y={heatGroup + 15}
                  x={referencePos.x}
                  fontSize="1em"
                  textAnchor={referencePos.align}
                  fill={"#444"}
                  fontWeight={"bold"}>
                  {lastProductionItem.reference ?? lastProductionItem.title}
                </text>}
              </g>
            );
          })}
        </g>
      </Chart>
      {openDetail && selectedProductionItem &&
      <Detail closed={() => setOpenDetail(false)} fullWidth={true} maxWidth={"xl"} title="Detail">
        <>
          {selectedProductionItem.__typename === "EAFProduction" && <HeatInformation productionItem={selectedProductionItem} height={500}/>}
          {selectedProductionItem.__typename === "LRFProduction" && <LRFHeatInformation productionItem={selectedProductionItem} height={500}/>}
          {selectedProductionItem.__typename === "CasterProduction" && <CasterHeatInformation productionItem={selectedProductionItem} height={500}/>}
          {selectedProductionItem.__typename === "StrandProduction" && <ProductionInformation productionItem={selectedProductionItem as IStrandProduction} height={500}/>}
        </>
      </Detail>
      }
    </Paper>
  );
};

export default GanttChart;
